import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { deliveryClient, govconClient } from '../../../lib/connect/clients';
import { makeEntityHooks } from '../_shared/hooks/makeEntityHooks';
import { entityErrorMessage } from '../_shared/lib/entityErrorMessage';
// Company Profiles — { id, companyId, name, cageCode, dunsNumber, uei, socioeconomicStatus, website }
export const companyProfileHooks = makeEntityHooks({
    key: 'company profile',
    // The programs pages cache profiles under their own keys: the own-org
    // resolution (Role=Prime autofill) and the prime dropdown must not serve
    // a deleted or renamed profile for minutes after a registry edit.
    extraInvalidateKeys: [
        'ownCompanyProfile',
        'companyProfile'
    ],
    list: async (req)=>{
        const resp = await deliveryClient.listCompanyProfiles({
            companyId: req.companyId,
            search: req.search ?? ''
        });
        return {
            items: resp.profiles
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertCompanyProfile(req);
        return resp.profile;
    },
    remove: async (id)=>{
        await deliveryClient.deleteCompanyProfile({
            id
        });
    },
    entityId: (c)=>c.id
});
// Contacts — affiliation fields + core contact scalars
export const contactHooks = makeEntityHooks({
    key: 'contact',
    list: async (req)=>{
        const resp = await deliveryClient.listContacts({
            companyId: req.companyId,
            search: req.search ?? '',
            // NULL-or-match server-side (SAD-75): a BU-scoped caller gets the
            // globally-visible contacts PLUS that BU's own, never a narrow-only
            // filter. Omitted/empty means no BU filtering at all.
            businessUnitId: req.businessUnitId ?? ''
        });
        return {
            items: resp.contacts
        };
    },
    upsert: async (req)=>{
        const isUpdate = !!req.id;
        const resp = await deliveryClient.upsertContact({
            ...req,
            // See companyProfileId/governmentOrganizationId above: CREATE sends
            // the plain fields (11/12); UPDATE routes through the presence-safe
            // companion fields (19/20) instead and omits the plain ones — ryugu
            // ignores 11/12 on update regardless of what's sent there, but
            // sending a stale value would be misleading to read on the wire.
            companyProfileId: isUpdate ? undefined : req.companyProfileId ?? '',
            governmentOrganizationId: isUpdate ? undefined : req.governmentOrganizationId ?? '',
            companyProfileIdV2: isUpdate ? req.companyProfileId : undefined,
            governmentOrganizationIdV2: isUpdate ? req.governmentOrganizationId : undefined
        });
        return resp.contact;
    },
    remove: async (id)=>{
        await deliveryClient.deleteContact({
            id
        });
    },
    entityId: (c)=>c.id
});
// Capabilities — company-wide catalog { id, companyId, name, description, isSystem,
// isRetired, retiredAt }. The program 'Add Capability' dropdown picks from these
// and lists with the default (retired names excluded); the registry tab asks for
// includeRetired and filters client-side.
const capabilityBaseHooks = makeEntityHooks({
    key: 'capability',
    list: async (req)=>{
        const resp = await deliveryClient.listCapabilities({
            companyId: req.companyId,
            search: req.search ?? '',
            includeRetired: req.includeRetired ?? false
        });
        return {
            items: resp.capabilities
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertCapability(req);
        return resp.capability;
    },
    remove: async (id)=>{
        await deliveryClient.deleteCapability({
            id
        });
    },
    entityId: (c)=>c.id
});
// Retire / reactivate are tenant-scoped lifecycle flips, not edits: the row and
// every program reference to it survive.
function useCapabilityLifecycle(action) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>{
            if (action === 'retire') await deliveryClient.retireCapability(req);
            else await deliveryClient.reactivateCapability(req);
        },
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'capability'
                ]
            });
            toast.success(action === 'retire' ? 'capability retired' : 'capability reactivated');
        },
        onError: (err)=>{
            toast.error(entityErrorMessage(err, 'save', 'capability'));
        }
    });
}
export const capabilityHooks = {
    ...capabilityBaseHooks,
    useRetire: ()=>useCapabilityLifecycle('retire'),
    useReactivate: ()=>useCapabilityLifecycle('reactivate')
};
// Contract Vehicles — { id, companyId, name, vehicleType, contractNumber }
export const contractVehicleHooks = makeEntityHooks({
    key: 'contract vehicle',
    list: async (req)=>{
        const resp = await deliveryClient.listContractVehicles(req);
        return {
            items: resp.vehicles
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertContractVehicle({
            id: req.id,
            companyId: req.companyId,
            name: req.name,
            vehicleType: req.vehicleType,
            contractNumber: req.contractNumber,
            agencyAcronym: req.agencyAcronym ?? '',
            status: req.status ?? 'ACTIVE',
            description: req.description ?? ''
        });
        return resp.vehicle;
    },
    remove: async (id)=>{
        await deliveryClient.deleteContractVehicle({
            id
        });
    },
    entityId: (v)=>v.id
});
// ---------------------------------------------------------------------------
// Contact ↔ account linkage (finalization WS6). The stored link is what the
// roster grant path resolves; email is discovery only. Link by explicit user
// id or by the contact's email (server refuses ambiguity + double-claims).
export function useLinkContactUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>deliveryClient.linkContactUser({
                companyId: args.companyId,
                contactId: args.contactId,
                userId: args.userId ?? '',
                email: args.email ?? ''
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'contact'
                ]
            });
            toast.success('Contact linked to their account');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to link contact');
        }
    });
}
export function useUnlinkContactUser() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>deliveryClient.unlinkContactUser(args),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'contact'
                ]
            });
            toast.success('Contact unlinked — future roster grants stop here; existing access stands');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to unlink contact');
        }
    });
}
// ---------------------------------------------------------------------------
// Labor category registry (Phase 2a, major-push/lcat-intelligence)
// ---------------------------------------------------------------------------
// Entries — lists return CURRENT versions only (superseded rows never reach
// the browser list; the drawer's History section reads the chain instead).
// An "update" mints a new version server-side, so the list must refetch to
// swap the row id — makeEntityHooks' invalidation covers it.
export const laborCategoryHooks = makeEntityHooks({
    key: 'labor category',
    // The Company drawer's Labor & rate evidence section (companyEvidenceHooks.ts)
    // keys its own query by companyProfileId and never unmounts across drawer
    // open/close within one SPA session (ScoreDetailDrawer keeps the last-shown
    // children mounted for its close animation) — without this, a freshly
    // created/edited/deleted entry never appears there short of a full page
    // reload.
    extraInvalidateKeys: [
        'company evidence'
    ],
    list: async (req)=>{
        const resp = await deliveryClient.listLaborCategoryEntries({
            companyId: req.companyId,
            search: req.search ?? '',
            companyProfileId: req.companyProfileId ?? '',
            contractVehicleId: req.contractVehicleId ?? '',
            includeRetired: req.includeRetired ?? false
        });
        return {
            items: resp.entries
        };
    },
    upsert: async (req)=>{
        const resp = await deliveryClient.upsertLaborCategoryEntry(req);
        return resp.entry;
    },
    remove: async (id)=>{
        await deliveryClient.deleteLaborCategoryEntry({
            id
        });
    },
    entityId: (e)=>e.id
});
// Retire closes an entry to NEW selection only: no new version, history and
// pinned rates untouched. Not a delete.
export function useRetireLaborCategoryEntry() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>deliveryClient.retireLaborCategoryEntry(args),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'labor category'
                ]
            });
            toast.success('Labor category retired — history and pinned rates are kept');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to retire labor category');
        }
    });
}
// The version chain for the drawer's History section, newest first.
export function useLaborCategoryHistory(entryId, companyId) {
    return useQuery({
        queryKey: [
            'labor category',
            'history',
            entryId
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.getLaborCategoryEntryHistory({
                id: entryId ?? '',
                companyId
            });
            return resp.entries;
        },
        enabled: !!entryId,
        staleTime: 30000
    });
}
// Candidates — the extraction-fed review queue. Decisions invalidate both
// the queue and the catalog (an accept mints an entry).
export function useLaborCategoryCandidates(companyId, status) {
    return useQuery({
        queryKey: [
            'labor category candidates',
            companyId,
            status
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listLaborCategoryCandidates({
                companyId,
                status
            });
            return resp.candidates;
        },
        staleTime: 15000
    });
}
function useCandidateDecision(method, successMessage) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (arg)=>deliveryClient[method](typeof arg === 'string' ? {
                id: arg
            } : arg),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'labor category candidates'
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    'labor category'
                ]
            });
            toast.success(successMessage);
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Decision failed');
        }
    });
}
export function useAcceptLaborCategoryCandidate() {
    return useCandidateDecision('acceptLaborCategoryCandidate', 'Candidate accepted into the catalog');
}
export function useRejectLaborCategoryCandidate() {
    return useCandidateDecision('rejectLaborCategoryCandidate', 'Candidate rejected');
}
// ---------------------------------------------------------------------------
// Rosetta: anchors + mappings (Phase 2b slice 2)
// ---------------------------------------------------------------------------
// Global SOC anchor catalog — reference data, cache aggressively.
export function useLaborCategoryAnchors(companyId, search = '', opts) {
    return useQuery({
        queryKey: [
            'lcat anchors',
            companyId,
            search
        ],
        enabled: opts?.enabled ?? true,
        queryFn: async ()=>{
            const resp = await deliveryClient.listLaborCategoryAnchors({
                companyId,
                search
            });
            return resp.anchors;
        },
        staleTime: 5 * 60000
    });
}
// Mapping edges — by entry (drawer section), by status (review queue), or by
// anchor (comparison view).
export function useLaborCategoryMappings(companyId, filter, opts) {
    return useQuery({
        queryKey: [
            'lcat mappings',
            companyId,
            filter
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listLaborCategoryMappings({
                companyId,
                fromEntryId: filter.fromEntryId ?? '',
                entryId: filter.entryId ?? '',
                toAnchorId: filter.toAnchorId ?? '',
                status: filter.status ?? ''
            });
            return resp.mappings;
        },
        enabled: opts?.enabled ?? true,
        staleTime: 15000
    });
}
/** `announceSuccess: false` mutes the success toast for callers that follow up with their own (auto-approve). */ export function useProposeLaborCategoryMapping({ announceSuccess = true } = {}) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>deliveryClient.proposeLaborCategoryMapping({
                companyId: req.companyId,
                fromEntryId: req.fromEntryId,
                toEntryId: req.toEntryId ?? '',
                toAnchorId: req.toAnchorId ?? '',
                mappingType: req.mappingType,
                // optional field: leave unset when the caller has no confidence value
                // — an explicit 0 now means "zero confidence", not "omitted".
                confidence: req.confidence,
                evidenceJson: req.evidenceJson ?? '{}'
            }),
        onSuccess: (resp)=>{
            qc.invalidateQueries({
                queryKey: [
                    'lcat mappings'
                ]
            });
            if (!resp.created && resp.mapping?.status === 'REJECTED') {
                toast.info('Unchanged evidence matches an earlier rejection. It remains rejected; change the target, relation or evidence to reconsider.');
            } else if (announceSuccess) {
                toast.success(resp.created ? 'Mapping proposed for review' : 'Existing mapping retained');
            }
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Proposal failed');
        }
    });
}
function useMappingMutation(mutationFn, successMessage) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn,
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'lcat mappings'
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    'program company staffing line'
                ]
            });
            toast.success(successMessage);
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Mapping update failed');
        }
    });
}
export function useApproveLaborCategoryMapping() {
    return useMappingMutation((request)=>deliveryClient.approveLaborCategoryMapping(request), 'Mapping approved');
}
export function useRejectLaborCategoryMapping() {
    return useMappingMutation((id)=>deliveryClient.rejectLaborCategoryMapping({
            id
        }), 'Mapping rejected');
}
export function useCorrectLaborCategoryMapping() {
    return useMappingMutation((request)=>deliveryClient.correctLaborCategoryMapping(request), 'Correction saved. Inspect the reviewed definitions before approval.');
}
export function useWithdrawLaborCategoryMapping() {
    return useMappingMutation((request)=>deliveryClient.withdrawLaborCategoryMapping(request), 'Mapping withdrawn; dependent selections remain unchanged');
}
// ---------------------------------------------------------------------------
// People Graph (Phase 3 slice 2): employment history + person merges
// ---------------------------------------------------------------------------
export function useContactEmployments(contactId, companyId) {
    return useQuery({
        queryKey: [
            'contact employments',
            contactId
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listContactEmployments({
                companyId,
                contactId: contactId ?? ''
            });
            return resp.employments;
        },
        enabled: !!contactId,
        staleTime: 30000
    });
}
export function useCreateContactEmployment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>deliveryClient.createContactEmployment({
                companyId: req.companyId,
                contactId: req.contactId,
                companyProfileId: req.companyProfileId ?? '',
                governmentOrganizationId: req.governmentOrganizationId ?? '',
                title: req.title ?? '',
                employmentType: req.employmentType ?? 'UNKNOWN',
                startDate: req.startDate,
                endDate: req.endDate
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'contact employments'
                ]
            });
            // The stored primary employer re-derives server-side (E5).
            qc.invalidateQueries({
                queryKey: [
                    'contact'
                ]
            });
            toast.success('Employment recorded');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to record employment');
        }
    });
}
export function useEndContactEmployment() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>deliveryClient.endContactEmployment(req),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'contact employments'
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    'contact'
                ]
            });
            toast.success('Employment ended');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to end employment');
        }
    });
}
export function useMergeContacts() {
    const qc = useQueryClient();
    const invalidate = ()=>{
        qc.invalidateQueries({
            queryKey: [
                'contact'
            ]
        });
        qc.invalidateQueries({
            queryKey: [
                'contact employments'
            ]
        });
        qc.invalidateQueries({
            queryKey: [
                'contact merge events'
            ]
        });
    };
    return useMutation({
        mutationFn: async (req)=>deliveryClient.mergeContacts(req),
        onSuccess: (_resp, req)=>{
            invalidate();
            // The reversal lives right in the receipt: Undo calls unmerge with the
            // loser id we just folded — the merge event reverses, the loser returns
            // to lists, and any carried platform link goes home.
            toast.success('Contacts merged', {
                duration: 20000,
                action: {
                    label: 'Undo',
                    onClick: ()=>{
                        deliveryClient.unmergeContacts({
                            companyId: req.companyId,
                            contactId: req.loserContactId
                        }).then(()=>{
                            invalidate();
                            toast.success('Merge reversed');
                        }).catch((err)=>{
                            toast.error(err instanceof Error ? err.message : 'Unmerge failed');
                        });
                    }
                }
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Merge failed');
        }
    });
}
export function useUnmergeContacts() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>deliveryClient.unmergeContacts(req),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'contact'
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    'contact employments'
                ]
            });
            qc.invalidateQueries({
                queryKey: [
                    'contact merge events'
                ]
            });
            toast.success('Merge reversed');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Unmerge failed');
        }
    });
}
export function useContactMergeEvents(contactId, companyId) {
    return useQuery({
        queryKey: [
            'contact merge events',
            contactId
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listContactMergeEvents({
                companyId,
                contactId: contactId ?? ''
            });
            return resp.events;
        },
        enabled: !!contactId,
        staleTime: 15000
    });
}
// ---------------------------------------------------------------------------
// Rate Book (Phase 4 slice 1): typed, append-only money
// ---------------------------------------------------------------------------
// Rate TYPES are never blurred in the UI either — a ceiling is a posted
// opening offer, an executed rate is what someone signed, and the delta
// between them is the whole point. Never render a single "market rate".
export const RATE_TYPE_LABELS = {
    CEILING: 'Ceiling (posted)',
    PROPOSED: 'Proposed (bid)',
    SUBK_AGREED: 'Subk agreed',
    BILLED: 'Billed (actual)',
    INFERRED: 'Inferred (estimate)',
    FLOOR: 'Floor',
    AWARDED: 'Awarded'
};
export const BURDEN_LABELS = {
    FULLY_BURDENED: 'fully burdened',
    BURDENED_NO_FEE: 'burdened, no fee',
    UNBURDENED: 'unburdened',
    UNKNOWN: 'burden unknown'
};
export function useRateObservations(companyId, req, opts) {
    return useQuery({
        queryKey: [
            'rate observations',
            companyId,
            req.lcatEntryId ?? 'all',
            req
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listRateObservations({
                companyId,
                lcatEntryId: req.lcatEntryId ?? '',
                anchorId: req.anchorId ?? '',
                companyProfileId: req.companyProfileId ?? '',
                contractVehicleId: req.contractVehicleId ?? '',
                observationType: req.observationType ?? '',
                rateUnit: req.rateUnit ?? '',
                burdenBasis: req.burdenBasis ?? '',
                normalizedPeriodKey: req.normalizedPeriodKey ?? '',
                relationIn: req.relationIn ?? [],
                acrossVersions: req.acrossVersions ?? false,
                includeCorrected: req.includeCorrected ?? false,
                limit: req.limit ?? 200
            });
            return resp.observations;
        },
        enabled: (opts?.enabled ?? true) && !!companyId,
        staleTime: 30000
    });
}
export function useCreateRateObservation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>deliveryClient.createRateObservation({
                companyId: req.companyId,
                lcatEntryId: req.lcatEntryId,
                observationType: req.observationType,
                amount: req.amount,
                // Empty = not stated: the backend stamps the assumption.
                rateUnit: req.rateUnit ?? '',
                burdenBasis: req.burdenBasis ?? '',
                // The recorder's own words for the period, stored verbatim.
                periodLabel: req.periodLabel ?? '',
                companyProfileId: req.companyProfileId ?? '',
                contractVehicleId: req.contractVehicleId ?? '',
                evidenceJson: req.evidenceJson ?? ''
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'rate observations'
                ]
            });
            toast.success('Rate recorded');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to record rate');
        }
    });
}
// A correction APPENDS a new row; the original stays readable as recorded.
// The type can never change (the server refuses it) — only the amount and,
// optionally, how the amount was measured.
export function useCorrectRateObservation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>deliveryClient.correctRateObservation({
                companyId: req.companyId,
                correctsId: req.correctsId,
                amount: req.amount,
                burdenBasis: req.burdenBasis ?? ''
            }),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'rate observations'
                ]
            });
            toast.success('Correction recorded — the original stays in history');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Correction failed');
        }
    });
}
export function useDeleteRateObservation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>deliveryClient.deleteRateObservation(req),
        onSuccess: ()=>{
            qc.invalidateQueries({
                queryKey: [
                    'rate observations'
                ]
            });
            toast.success('Rate removed');
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to remove rate');
        }
    });
}
// ---------------------------------------------------------------------------
// Pricing sheet review (Phase 4 slice 2b)
//
// An uploaded sheet is staged as a change set; nothing reaches the rate book
// until a human accepts a block. These hooks are deliberately NOT built on
// makeEntityHooks: a review is not an entity you upsert, it is a decision
// queue, and the two mutations here (rule on a block, commit the review) do
// not share that shape.
// ---------------------------------------------------------------------------
export function usePricingSheetReviews(companyId, req, opts) {
    return useQuery({
        queryKey: [
            'pricing sheet reviews',
            companyId,
            req?.contractVehicleId ?? 'all',
            req?.includeApplied ?? false
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.listPricingSheetReviews({
                companyId,
                contractVehicleId: req?.contractVehicleId ?? '',
                includeApplied: req?.includeApplied ?? false
            });
            return resp.reviews;
        },
        enabled: (opts?.enabled ?? true) && Boolean(companyId)
    });
}
export function usePricingSheetReview(companyId, changeSetId, opts) {
    return useQuery({
        queryKey: [
            'pricing sheet review',
            companyId,
            changeSetId
        ],
        queryFn: async ()=>{
            const resp = await deliveryClient.getPricingSheetReview({
                companyId,
                changeSetId
            });
            return resp;
        },
        enabled: (opts?.enabled ?? true) && Boolean(companyId) && Boolean(changeSetId)
    });
}
// Corrects a staged block's interpretation (or a reading's header mapping) and
// resolves its company / labor category BEFORE commit. Never settles the block.
export function useCorrectPricingSheetBlock() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const { changeSetId: _changeSetId, ...req } = args;
            const resp = await deliveryClient.correctPricingSheetBlock({
                note: '',
                ...req
            });
            return resp.block;
        },
        onSuccess: (_block, args)=>{
            void qc.invalidateQueries({
                queryKey: [
                    'pricing sheet review',
                    args.companyId,
                    args.changeSetId
                ]
            });
        }
    });
}
export function useResolvePricingSheetBlock() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await deliveryClient.resolvePricingSheetBlock({
                companyId: args.companyId,
                itemId: args.itemId,
                resolution: args.resolution,
                note: args.note ?? ''
            });
            return resp.block;
        },
        onSuccess: (_block, args)=>{
            // Both keys: the detail view's counts change, and so does the list's
            // "waiting on me" badge.
            void qc.invalidateQueries({
                queryKey: [
                    'pricing sheet review',
                    args.companyId,
                    args.changeSetId
                ]
            });
            void qc.invalidateQueries({
                queryKey: [
                    'pricing sheet reviews'
                ]
            });
        },
        onError: (err)=>toast.error(err.message)
    });
}
export function useApplyPricingSheetReview() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await deliveryClient.applyPricingSheetReview(args);
            return resp;
        },
        onSuccess: (resp, args)=>{
            void qc.invalidateQueries({
                queryKey: [
                    'pricing sheet review',
                    args.companyId,
                    args.changeSetId
                ]
            });
            void qc.invalidateQueries({
                queryKey: [
                    'pricing sheet reviews'
                ]
            });
            // Committing a review MINTS RATES, so the rate book is now stale.
            void qc.invalidateQueries({
                queryKey: [
                    'rate observations'
                ]
            });
            // Warnings are not errors, but a partial apply that reports nothing reads
            // as a total one — surface them rather than swallowing them.
            if (resp.warnings.length > 0) {
                toast.warning(`Committed with ${resp.warnings.length} warning(s)`, {
                    description: resp.warnings[0]
                });
            }
        },
        onError: (err)=>toast.error(err.message)
    });
}
// Reads what a pricing sheet says about itself. Writes nothing — the upload flow
// calls this so it can show a person what was found and ask whether it is right,
// instead of asking them to type in facts the document already states.
export function useInspectPricingSheet() {
    return useMutation({
        // Takes BYTES, not a File: the caller reads the file once and the same bytes
        // serve the inspection and the ingest. A vehicle-wide workbook is megabytes,
        // and reading it twice to ask two questions about it is waste the user pays
        // for in latency.
        mutationFn: async (args)=>govconClient.inspectPricingSheet({
                fileData: args.bytes,
                filename: args.filename,
                mimeType: args.mimeType,
                tenantId: args.companyId
            }),
        onError: (err)=>toast.error(err.message)
    });
}
// Reads a staged sheet AGAIN from the artifact it was uploaded as. Nobody has to
// find the file: the artifact id travels on every rate the sheet staged.
export function useReExtractPricingSheet() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await govconClient.reExtractPricingSheet({
                tenantId: args.companyId,
                sourceDocumentId: args.sourceDocumentId,
                contractVehicleId: args.contractVehicleId ?? '',
                companyProfileId: args.companyProfileId ?? '',
                observationType: args.observationType,
                seedMissingCategories: args.seedMissingCategories ?? true,
                supersedesChangeSetId: args.supersedesChangeSetId ?? ''
            });
            return resp.runId;
        },
        onSuccess: ()=>{
            void qc.invalidateQueries({
                queryKey: [
                    'pricing sheet reviews'
                ]
            });
        },
        onError: (err)=>toast.error(err.message)
    });
}
// Rules on MANY staged items in one go.
//
// Its own mutation rather than a loop over useResolvePricingSheetBlock: a single
// useMutation instance tracks ONE in-flight mutation, so 42 rapid mutate() calls
// drop each other and only a couple land. That bug shipped — accepting 42
// categories left all 42 Open, and the browser run is what caught it.
//
// Sequential rather than parallel: these are decisions on shared catalog data and
// a burst of 42 writes is worth pacing. It also means a failure stops the run with
// a count of what already landed, instead of leaving an unknown subset applied.
export function useResolvePricingSheetBlocks() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            let done = 0;
            for (const itemId of args.itemIds){
                await deliveryClient.resolvePricingSheetBlock({
                    companyId: args.companyId,
                    itemId,
                    resolution: args.resolution,
                    note: args.note ?? ''
                });
                done++;
            }
            return done;
        },
        onSuccess: (done, args)=>{
            void qc.invalidateQueries({
                queryKey: [
                    'pricing sheet review',
                    args.companyId,
                    args.changeSetId
                ]
            });
            void qc.invalidateQueries({
                queryKey: [
                    'pricing sheet reviews'
                ]
            });
            toast.success(`${done} labor categor${done === 1 ? 'y' : 'ies'} accepted`);
        },
        onError: (err)=>toast.error(err.message)
    });
}
// ---------------------------------------------------------------------------
// Company lineage — aliases (former names, DBAs) and dated M&A edges.
//
// The decision these implement is 18-tenant-identity §8, model (b): a human
// records the fact once, the old spelling then RESOLVES to the surviving
// profile at every match point (upload preflight, staged-change apply), and
// nothing merges — both eras keep their own rates. Writes take registries
// authority server-side; these hooks just carry honest errors back.
const LINEAGE_KEY = 'company lineage';
export function useCompanyAliases(companyProfileId) {
    return useQuery({
        queryKey: [
            LINEAGE_KEY,
            'aliases',
            companyProfileId
        ],
        enabled: Boolean(companyProfileId),
        queryFn: async ()=>{
            const resp = await deliveryClient.listCompanyAliases({
                companyId: '',
                companyProfileId
            });
            return resp.aliases;
        }
    });
}
export function useUpsertCompanyAlias() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (alias)=>{
            const resp = await deliveryClient.upsertCompanyAlias({
                companyId: '',
                alias
            });
            return resp.alias;
        },
        onSuccess: ()=>void qc.invalidateQueries({
                queryKey: [
                    LINEAGE_KEY
                ]
            }),
        onError: (err)=>toast.error(err.message)
    });
}
export function useDeleteCompanyAlias() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (aliasId)=>{
            await deliveryClient.deleteCompanyAlias({
                companyId: '',
                aliasId
            });
        },
        onSuccess: ()=>void qc.invalidateQueries({
                queryKey: [
                    LINEAGE_KEY
                ]
            }),
        onError: (err)=>toast.error(err.message)
    });
}
export function useCompanyRelationships(companyProfileId) {
    return useQuery({
        queryKey: [
            LINEAGE_KEY,
            'relationships',
            companyProfileId
        ],
        enabled: Boolean(companyProfileId),
        queryFn: async ()=>{
            const resp = await deliveryClient.listCompanyRelationships({
                companyId: '',
                companyProfileId
            });
            return resp.relationships;
        }
    });
}
export function useCreateCompanyRelationship() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (relationship)=>{
            const resp = await deliveryClient.createCompanyRelationship({
                companyId: '',
                relationship: {
                    ...relationship,
                    notes: relationship.notes ?? ''
                }
            });
            return resp.relationship;
        },
        onSuccess: ()=>void qc.invalidateQueries({
                queryKey: [
                    LINEAGE_KEY
                ]
            }),
        onError: (err)=>toast.error(err.message)
    });
}
export function useDeleteCompanyRelationship() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (relationshipId)=>{
            await deliveryClient.deleteCompanyRelationship({
                companyId: '',
                relationshipId
            });
        },
        onSuccess: ()=>void qc.invalidateQueries({
                queryKey: [
                    LINEAGE_KEY
                ]
            }),
        onError: (err)=>toast.error(err.message)
    });
}
// SAD-451 RATE-07: Proposal pricing's version-pinned picks of rate evidence.
// A selection never writes to the Rate Book; it only reads observations.
export function useRateSelections(projectId, companyId, scenarioId, enabled = true) {
    return useQuery({
        queryKey: [
            'rate selections',
            projectId,
            scenarioId
        ],
        queryFn: async ()=>(await deliveryClient.listRateSelections({
                projectId,
                companyId,
                consumerId: scenarioId
            })).selections,
        enabled: enabled && !!projectId && !!companyId && !!scenarioId
    });
}
export function useSelectRate(projectId, scenarioId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>(await deliveryClient.selectRateObservation({
                projectId,
                companyId: req.companyId,
                consumerId: scenarioId,
                consumerRef: req.consumerRef,
                observationId: req.observationId,
                selectedAmount: req.selectedAmount ?? 0,
                assumptionsJson: req.assumptionsJson ?? ''
            })).selection,
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    'rate selections',
                    projectId,
                    scenarioId
                ]
            })
    });
}
export function useAcknowledgeRateReview(projectId, scenarioId) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (req)=>(await deliveryClient.acknowledgeRateSelectionReview({
                ...req,
                projectId,
                consumerId: scenarioId
            })).selection,
        onSuccess: ()=>qc.invalidateQueries({
                queryKey: [
                    'rate selections',
                    projectId,
                    scenarioId
                ]
            })
    });
}
export function useRateEvidenceEntries(companyId) {
    return useQuery({
        queryKey: [
            'rate evidence entries',
            companyId
        ],
        queryFn: async ()=>(await deliveryClient.listLaborCategoryEntries({
                companyId
            })).entries,
        enabled: !!companyId,
        staleTime: 30000
    });
}
